const STORAGE_KEY = 'weeduc_school_group_assignments_v2';

function nowIso() {
    return new Date().toISOString();
}

function createId() {
    return `ga_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function delay(ms = 180) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function readStore() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            // migrate v1 if present
            const legacy = localStorage.getItem('weeduc_school_group_assignments_v1');
            if (legacy) {
                const parsed = JSON.parse(legacy);
                if (Array.isArray(parsed)) {
                    const migrated = parsed.map((row) => normalize({
                        ...row,
                        resourceType: 'group',
                        resourceId: row.groupId || row.resourceId,
                    }));
                    writeStore(migrated);
                    return migrated;
                }
            }
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function writeStore(rows) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

function normalize(payload = {}) {
    const userIds = Array.isArray(payload.userIds)
        ? [...new Set(payload.userIds.map((id) => String(id)).filter(Boolean))]
        : [];
    const resourceType = payload.resourceType === 'course' ? 'course' : 'group';
    const resourceId = String(
        payload.resourceId || payload.groupId || payload.courseId || '',
    );
    return {
        resourceType,
        resourceId,
        groupId: resourceType === 'group' ? resourceId : null,
        courseId: resourceType === 'course' ? resourceId : null,
        userIds,
    };
}

function enrich(row) {
    if (!row) return null;
    const normalized = {
        ...row,
        resourceType: row.resourceType === 'course' ? 'course' : 'group',
        resourceId: String(row.resourceId || row.groupId || row.courseId || ''),
    };
    return {
        ...normalized,
        groupId: normalized.resourceType === 'group' ? normalized.resourceId : normalized.groupId,
        usersCount: (normalized.userIds || []).length,
    };
}

export default {
    async list(params = {}) {
        await delay();
        let rows = readStore().map(enrich).filter(Boolean);
        const search = String(params.search || '').trim().toLowerCase();
        const groupMap = params.groupMap || {};
        const courseMap = params.courseMap || {};
        const userMap = params.userMap || {};

        if (search) {
            rows = rows.filter((row) => {
                const resourceName = row.resourceType === 'course'
                    ? String(courseMap[row.resourceId]?.name || '').toLowerCase()
                    : String(groupMap[row.resourceId]?.name || '').toLowerCase();
                const userHit = (row.userIds || []).some((uid) => {
                    const user = userMap[uid] || {};
                    const name = String(user.name || '').toLowerCase();
                    const email = String(user.email || '').toLowerCase();
                    return name.includes(search) || email.includes(search) || String(uid).includes(search);
                });
                return resourceName.includes(search)
                    || String(row.resourceId).includes(search)
                    || userHit;
            });
        }

        if (params.resourceType) {
            rows = rows.filter((r) => r.resourceType === params.resourceType);
        }
        if (params.groupId) {
            rows = rows.filter(
                (r) => r.resourceType === 'group'
                    && String(r.resourceId) === String(params.groupId),
            );
        }
        if (params.resourceId) {
            rows = rows.filter((r) => String(r.resourceId) === String(params.resourceId));
        }

        rows = rows.map((row) => {
            const resource = row.resourceType === 'course'
                ? (courseMap[row.resourceId] || {})
                : (groupMap[row.resourceId] || {});
            const users = (row.userIds || []).map((uid) => userMap[uid] || { id: uid, name: uid });
            return {
                ...row,
                resourceName: resource.name
                    || resource.comercialName
                    || (row.resourceType === 'course'
                        ? `Curso ${row.resourceId}`
                        : `Grupo ${row.resourceId}`),
                groupName: row.resourceType === 'group'
                    ? (resource.name || `Grupo ${row.resourceId}`)
                    : null,
                userLabels: users.map((u) => u.name || u.email || u.id),
            };
        });

        return { content: rows, totalElements: rows.length };
    },

    async getById(id) {
        await delay();
        const row = readStore().find((r) => String(r.id) === String(id));
        if (!row) throw new Error('Vínculo não encontrado');
        return enrich(row);
    },

    async create(payload) {
        await delay();
        const data = normalize(payload);
        if (!data.resourceId) throw new Error('Recurso obrigatório');
        if (!data.userIds.length) throw new Error('Informe ao menos um aluno');
        const row = {
            id: createId(),
            ...data,
            dateInsert: nowIso(),
            dateUpdate: nowIso(),
        };
        const rows = readStore();
        rows.unshift(row);
        writeStore(rows);
        return enrich(row);
    },

    async update(id, payload) {
        await delay();
        const rows = readStore();
        const index = rows.findIndex((r) => String(r.id) === String(id));
        if (index < 0) throw new Error('Vínculo não encontrado');
        const data = normalize(payload);
        if (!data.resourceId) throw new Error('Recurso obrigatório');
        if (!data.userIds.length) throw new Error('Informe ao menos um aluno');
        rows[index] = {
            ...rows[index],
            ...data,
            id: rows[index].id,
            dateInsert: rows[index].dateInsert,
            dateUpdate: nowIso(),
        };
        writeStore(rows);
        return enrich(rows[index]);
    },

    async remove(id) {
        await delay();
        writeStore(readStore().filter((r) => String(r.id) !== String(id)));
        return { success: true };
    },
};
